<script setup>
import { computed, h, ref, watch } from "vue";

import clsx from "clsx";
import { RouterLink, useRouter } from "vue-router";

import DefaultAvatarIcon from "@/components/Icons/DefaultAvatarIcon.vue";
import DefaultGroupIcon from "@/components/Icons/DefaultGroupIcon.vue";
import Reputation from "@/components/Icons/Reputation.vue";
import Verified from "@/components/Icons/Verified.vue";
import { getReputationTooltip } from "@/helpers/reputationTooltipHelper.js";
import { BaseURL } from "@/global/baseUrls.js";

/**
 * @prop {String} src - Explicit avatar image URL; takes precedence over `storageId` when set.
 * @prop {String} href - When set, the avatar renders as a RouterLink to this path instead of a plain div.
 * @prop {String} storageId - Storage id used to build the avatar URL (via BaseURL) when `src` isn't provided.
 * @prop {String} size - Named size variant ('md' | 'lg' | 'xl'); also used to look up the badge offset.
 * @prop {String} classes - Extra CSS classes merged onto the avatar element.
 * @prop {Boolean} verified - Shows the verified/reputation badge overlay in the bottom-right corner.
 * @prop {Number} width - Width (px) of the verified/reputation badge icon.
 * @prop {Number} height - Height (px) of the verified/reputation badge icon.
 * @prop {Boolean} isAdmin - When verified is true, picks the Reputation icon instead of the Verified icon.
 * @prop {Boolean} isOrg - Renders the group fallback icon instead of the user fallback icon, and skips the rating ring.
 * @prop {Number} rating - Civility rating driving the ring color and tooltip text; null/undefined hides the ring.
 * @prop {Boolean} isCurrentUser - Passed to the tooltip helper to phrase the tooltip for "you" vs another user.
 * @prop {Number} civicScore - Unused/reserved prop (not referenced elsewhere in this component).
 * @prop {String} badgeIcon - Key into BADGE_ICON_MAP (or a raw storage path) for an extra corner badge image.
 * @prop {String} target - Native `target` attribute applied when rendered as a RouterLink (via `href`).
 */
const props = defineProps({
  src: {
    type: String,
    default: "",
  },
  href: {
    type: String,
    default: "",
  },
  storageId: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "",
  },
  classes: {
    type: String,
    default: "",
  },
  verified: {
    type: Boolean,
    default: false,
  },
  width: {
    type: Number,
    default: 16,
  },
  height: {
    type: Number,
    default: 16,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  isOrg: {
    type: Boolean,
    default: false,
  },
  // Add rating and isCurrentUser props for tooltip
  rating: {
    type: Number,
    default: null,
  },
  isCurrentUser: {
    type: Boolean,
    default: false,
  },
  civicScore: {
    type: Number,
    default: 0
  },
  badgeIcon: {
    type: String,
    default: "",
  },
  target: {
    type: String,
    default: "",
  },
});


/** Resolves the image URL to use: explicit `src` first, else built from `storageId` + BaseURL, else empty. */
const getSrc = computed(() => {
  if (props.src) {
    return props.src;
  }
  if (props.storageId) {
    return BaseURL + props.storageId;
  }
  return "";
});

// Tracks a load failure on the real image above so the template can fall
// back to the inline default icon — resetting whenever the src itself
// changes so a new avatar gets its own chance to load.
const imgFailed = ref(false);
/** Resets `imgFailed` whenever the resolved source changes, giving a new avatar URL a fresh chance to load. */
watch(getSrc, () => { imgFailed.value = false; });
/** True when there's no resolvable image source, or the real `<img>` failed to load — tells the template to render the fallback icon component instead. */
const showFallbackIcon = computed(() => !getSrc.value || imgFailed.value);
/** Which fallback icon component to use when there's no/failed image: group icon for orgs, person icon otherwise. */
const FallbackIcon = computed(() => (props.isOrg ? DefaultGroupIcon : DefaultAvatarIcon));

// Compute tooltip text if rating is provided
/** Tooltip text describing the rating, built via the reputation tooltip helper; empty string when no rating is provided. */
const tooltipText = computed(() => {
  if (props.rating !== null && props.rating !== undefined) {
    return getReputationTooltip(props.rating, props.isCurrentUser);
  }
  return "";
});
/** Whether `props.rating` is a usable (non-null, numeric) value; gates whether the rating ring is shown. */
const hasRating = computed(() => props.rating !== null && props.rating !== undefined && !Number.isNaN(Number(props.rating)));

/** Maps known `badgeIcon` prop keys to their CDN image URLs for the extra corner badge. */
const BADGE_ICON_MAP = {
  verified: "https://cdn.digicollect.com/cdn/launchpad/verified.png",
  heart: "https://cdn.digicollect.com/cdn/launchpad/heart.png",
  flame: "https://cdn.digicollect.com/cdn/launchpad/flame.png",
  star: "https://cdn.digicollect.com/cdn/launchpad/star.png",
  crown: "https://cdn.digicollect.com/cdn/launchpad/crown.png",
  sparkles: "https://cdn.digicollect.com/cdn/launchpad/sparkles.png",

};
/** Resolves `props.badgeIcon` to a displayable URL: a known key from BADGE_ICON_MAP, a raw storage path via BaseURL, or empty if unset. */
const badgeIconSrc = computed(() => { const k = props.badgeIcon ?? ""; return BADGE_ICON_MAP[k] || (k ? `${BaseURL}${k}` : ""); });

//select civic color
/** Pixel diameters for the named `size` prop values, used to compute the badge's offset. */
const AVATAR_SIZE_MAP = { md: 60, lg: 66, xl: 96 };

// Place badge center at the 45° point on the circle edge: offset from edge = diameter × (1 - 1/√2)/2 ≈ diameter × 0.146
/** CSS bottom/right offset (px) placing the verified/reputation badge at the circle's 45° edge point, sized for the current avatar size. */
const badgePosition = computed(() => {
  const avatarSize = AVATAR_SIZE_MAP[props.size] || 40;
  const offset = avatarSize * 0.146 - props.width / 2 - 4;
  return `${offset}px`;
});

/** Maps `props.rating` into a traffic-light color (green/yellow/red) for the avatar's rating ring. */
const ringColor = computed(() => {
  const score = Number(props.rating) || 0;
  if (score >= 40 && score <= 100) {
    return "#22c55e";  // green
  }

  if (score >= -39 && score <= 39) {
    // return "#facc15";  // yellow
    // return "rgb(34 194 94)";
    // return "var(--color-quick-link-icon-bg)";
    return "#facc15";
  }

  if (score >= -100 && score <= -40) {
    return "#ef4444";  // red
  }

  return "#facc15"; // default fallback
});

/** Inline style for the ring wrapper: a solid border colored by `ringColor` when a rating exists and it isn't an org avatar, otherwise no styling. */
const ringStyle = computed(() => {
  if (props.isOrg || !hasRating.value) return {};
  // return { '--avatar-ring-background': `conic-gradient(${ringColor.value} 0%, ${ringColor.value} 100%)` };
  return { border: `2px solid ${ringColor.value}` };
});

// Ensure broken external images don't cascade errors or cause repeated reflows —
// swaps to the inline default icon (see showFallbackIcon) instead of retrying
// with another <img src>.
const newOnImgError = (e) => {
  if (import.meta.env.DEV && e?.target) {
    const originalSrc = e.target.src;
    const isInstagramCdn = originalSrc.includes('cdninstagram.com') || originalSrc.includes('scontent-');
    const isKnownExternalDomain = originalSrc.includes('sfstandard.com') || originalSrc.includes('content.sfstandard.com');
    if (!isInstagramCdn && !isKnownExternalDomain) {
      console.debug(`[BaseAvatar] Unexpected image load failure: ${originalSrc}`);
    }
  }
  imgFailed.value = true;
};
</script>

<template>
  <!-- :title="tooltipText || undefined" -->
  <!-- Ring wrapper; border color/visibility comes from ringStyle (rating-based), skipped entirely for org avatars or missing ratings -->
  <div class="new-avatar-ring"
    :style="ringStyle"
    >
    <!-- Renders as a RouterLink when `href` is given, otherwise a plain div; click is stopped so it doesn't bubble to parent handlers (e.g. post click) -->
    <component @click.stop :is="props.href ? 'RouterLink' : 'div'" :to="props.href || undefined" :target="props.target || undefined" class="new-avatar"
      :class="clsx({ 'border-none': isOrg }, size, classes)">
      <!-- Real avatar image; only rendered while it hasn't failed to load and a source is resolvable -->
      <img v-if="!showFallbackIcon" class="new-avatar-img" :src="getSrc" alt="user's avatar" loading="lazy" decoding="async" @error="newOnImgError" />
      <!-- Fallback icon (person or group, via FallbackIcon) shown when there's no image or it failed to load -->
      <component :is="FallbackIcon" v-else class="new-avatar-img" />
      <!-- Verified/reputation badge overlay, shown only when the `verified` prop is true; icon choice depends on isAdmin -->
      <span v-if="verified" class="absolute flex items-center z-10" :style="{ bottom: badgePosition, right: badgePosition }">
        <component :is="isAdmin ? Reputation : Verified" :style="{ width: `${width}px`, height: `${height}px` }" />
      </span>
    </component>
    <!-- Extra corner badge image, rendered only when badgeIconSrc resolves to a non-empty URL -->
    <span v-if="badgeIconSrc" class="new-avatar-badge">
      <img :src="badgeIconSrc" alt="badge" loading="lazy" decoding="async" />
    </span>
  </div>
</template>

<style scoped lang="scss">
.new-avatar {
  display: block;
  width: 40px;
  height: 40px;
  // border: 1px solid #71767b;
  position: relative;
  z-index: 1;
  border-radius: 100%;
  cursor: pointer;

  &-img {
    width: 100%;
    height: 100%;
    // border-radius: 50%;
    border-radius: 5px;
    object-fit: cover;
  }

  &.lg {
    width: 66px;
    height: 66px;
  }

  &.xl {
    width: 96px;
    height: 96px;
  }

  &.md {
    width: 60px;
    height: 60px;
  }
}

.new-avatar-ring {
  position: relative;
  isolation: isolate;
  padding: 0;
  // border-radius: 50%;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  height: fit-content;
}

.new-avatar-badge {
  position: absolute;
  bottom: -5px;
  right: -5px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.new-avatar-ring::before {
  content: "";
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  background: var(--avatar-ring-background, transparent);
  z-index: 0;
}
</style>
